import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, Scale, Dumbbell, Salad, Activity, Zap, Moon, Smile, Calculator, HeartPulse, Droplets, Flame } from "lucide-react";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import { getGoal, setGoal, clearGoal, prioritize } from "../lib/goal";
import { useLocale } from "../context/LocaleContext";
import { useAuth } from "../context/AuthContext";
import { ContentCard } from "../components/ContentCard";
import { Newsletter } from "../components/Newsletter";
import { Mascot } from "../components/Mascot";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";

const HERO_IMG = "https://images.pexels.com/photos/5622210/pexels-photo-5622210.jpeg";
const EXPLORE = ["nutrition", "fitness", "recipes", "exercises", "body", "sports", "sleep", "weight"];

function Ico({ name, className }) { const C = Icons[name] || Icons.Circle; return <C className={className} />; }

function Row({ title, items, viewAllTo, viewAllLabel }) {
  if (!items?.length) return null;
  return (
    <section className="container-page mt-16">
      <div className="flex items-end justify-between">
        <h2 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{title}</h2>
        {viewAllTo && (
          <Link to={viewAllTo} className="group flex items-center gap-1 text-sm font-semibold text-emerald-700">
            {viewAllLabel} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        )}
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((it, i) => <ContentCard key={it.id} item={it} index={i} />)}
      </div>
    </section>
  );
}

export default function HomePage() {
  const { locale, t, lp, sp } = useLocale();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [goal, setGoalState] = useState(getGoal());

  useEffect(() => {
    api.get(`/api/public/home?locale=${locale}`).then(({ data }) => setData(data)).catch(() => {});
    track("page_view", { locale });
  }, [locale]);

  useEffect(() => { if (user?.goal) setGoalState(user.goal); }, [user]);

  function chooseGoal(key) {
    setGoal(key); setGoalState(key);
    track("goal_selected", { locale, props: { goal: key } });
  }
  function removeGoal() { clearGoal(); setGoalState(null); }

  const byslug = Object.fromEntries((data?.sections || []).map((s) => [s.slug, s]));
  const exploreCards = EXPLORE.map((s) => byslug[s]).filter(Boolean);

  const goals = [
    { key: "goalLose", icon: Scale }, { key: "goalMuscle", icon: Dumbbell },
    { key: "goalEat", icon: Salad }, { key: "goalFit", icon: Activity },
    { key: "goalPerf", icon: Zap }, { key: "goalSleep", icon: Moon }, { key: "goalFeel", icon: Smile },
  ];
  const tools = [
    { key: "toolBmi", slug: "bmi", icon: Calculator }, { key: "toolCalories", slug: "calories", icon: Flame },
    { key: "toolProtein", slug: "protein", icon: Dumbbell }, { key: "toolHydration", slug: "hydration", icon: Droplets }, { key: "toolMacros", slug: "macros", icon: Salad },
  ];
  const bodyTopics = [
    { key: "bodyMuscles", icon: Dumbbell }, { key: "bodyMetabolism", icon: Flame },
    { key: "bodyHydration", icon: Droplets }, { key: "bodyRecovery", icon: HeartPulse }, { key: "bodyEnergy", icon: Zap },
  ];

  return (
    <>
      <Seo seo={data?.seo} title="LIVYVIO — Live better, every day." description={t("heroSubtitle")} />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="container-page grid items-center gap-10 py-16 lg:grid-cols-2 lg:py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="min-w-0">
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.15em] text-emerald-700">{t("heroKicker")}</span>
            <h1 className="mt-5 font-heading text-4xl font-black leading-[1.05] tracking-tight text-slate-900 break-words sm:text-5xl lg:text-6xl">
              {t("heroTitleLead")} <span className="brand-gradient-text">{t("heroTitleAccent")}</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">{t("heroSubtitle")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full bg-emerald-600 px-7 hover:bg-emerald-700 transition-transform hover:scale-105 active:scale-95" data-testid="hero-cta-explore">
                <Link to={lp(`/${sp("articles")}`)}>{t("ctaExplore")}</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-slate-300 px-7" data-testid="hero-cta-goal">
                <Link to={lp(`/${sp("goals")}`)}>{t("ctaGoal")}</Link>
              </Button>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative">
            <div className="overflow-hidden rounded-[2rem] border border-slate-200 shadow-xl">
              <img src={HERO_IMG} alt="Healthy active lifestyle" className="h-[360px] w-full object-cover sm:h-[460px]" />
            </div>
            <div className="absolute -bottom-6 -right-3 animate-floaty rounded-3xl border border-emerald-100 bg-white/90 p-3 shadow-lg backdrop-blur">
              <Mascot className="h-16 w-16" alt="LIVYVIO mascot" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Goal-aware banner */}
      {goal && (
        <section className="container-page">
          <div data-testid="goal-banner" className="flex flex-wrap items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-3">
            <span className="text-sm font-semibold text-emerald-800">{t("yourFocus")}: {t(goal)}</span>
            <div className="ml-auto flex gap-2">
              <Link to={lp(`/${sp("goals")}`)} className="rounded-full border border-emerald-300 bg-white px-3 py-1 text-xs font-semibold text-emerald-700" data-testid="goal-change">{t("changeGoal")}</Link>
              <button onClick={removeGoal} className="rounded-full px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100" data-testid="goal-clear">{t("clearGoal")}</button>
            </div>
          </div>
        </section>
      )}

      {/* Goal selector */}
      <section className="container-page mt-6">
        <h2 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{t("goalsTitle")}</h2>
        <p className="mt-1 text-slate-500">{t("goalsSub")}</p>
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {goals.map((g, i) => (
            <motion.div key={g.key} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(i * 0.04, 0.3) }}>
              <Link to={lp(`/${sp("goals")}`)} onClick={() => chooseGoal(g.key)} data-testid={`goal-${g.key}`}
                className="group flex h-full flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-center transition-all hover:-translate-y-1 hover:border-emerald-300 hover:shadow-md">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 transition-colors group-hover:bg-emerald-600 group-hover:text-white"><g.icon className="h-5 w-5" /></span>
                <span className="text-sm font-semibold text-slate-800">{t(g.key)}</span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Explore categories */}
      <section className="container-page mt-16">
        <h2 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{t("exploreTitle")}</h2>
        <p className="mt-1 text-slate-500">{t("exploreSub")}</p>
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {exploreCards.map((s, i) => (
            <motion.div key={s.slug} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(i * 0.04, 0.4) }}>
              <Link to={lp(`/${s.path}`)} data-testid={`section-tile-${s.slug}`}
                className="group relative flex h-full min-h-[150px] flex-col justify-end overflow-hidden rounded-2xl border border-slate-200 p-5 transition-all hover:-translate-y-1 hover:shadow-lg">
                <img src={s.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20 transition-opacity group-hover:opacity-30" />
                <div className="relative">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/90 text-emerald-600 shadow-sm"><Ico name={s.icon} className="h-5 w-5" /></span>
                  <h3 className="mt-3 font-heading text-base font-bold text-slate-900">{s.title}</h3>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Tools preview */}
      <section className="container-page mt-16">
        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6 sm:p-10">
          <div className="flex items-end justify-between">
            <div>
              <h2 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{t("toolsTitle")}</h2>
              <p className="mt-1 text-slate-500">{t("toolsSub")}</p>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {tools.map((tool) => (
              <Link key={tool.key} to={lp(`/${sp("calculators")}/${tool.slug}`)} data-testid={`tool-${tool.key}`}
                className="group relative rounded-2xl border border-slate-200 bg-white p-4 transition-all hover:-translate-y-1 hover:shadow-md">
                
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600"><tool.icon className="h-5 w-5" /></span>
                <h3 className="mt-3 text-sm font-semibold text-slate-800">{t(tool.key)}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured dynamic content */}
      <Row title={t("featArticles")} items={prioritize(data?.featured?.articles, goal)} viewAllTo={lp(`/${sp("articles")}`)} viewAllLabel={t("viewAll")} />
      <Row title={t("featExercises")} items={prioritize(data?.featured?.exercises, goal)} viewAllTo={lp(`/${sp("exercises")}`)} viewAllLabel={t("viewAll")} />
      <Row title={t("featRecipes")} items={prioritize(data?.featured?.recipes, goal)} viewAllTo={lp(`/${sp("recipes")}`)} viewAllLabel={t("viewAll")} />

      {/* Understand your body */}
      <section className="container-page mt-20">
        <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:items-center">
          <div>
            <h2 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{t("bodyTitle")}</h2>
            <p className="mt-3 max-w-md text-slate-600">{t("bodySub")}</p>
            <Button asChild variant="outline" className="mt-6 rounded-full border-slate-300" data-testid="body-cta"><Link to={lp(`/${sp("body")}`)}>{t("viewAll")}</Link></Button>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {bodyTopics.map((b, i) => (
              <motion.div key={b.key} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(i * 0.05, 0.3) }}>
                <Link to={lp(`/${sp("body")}`)} className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition-all hover:-translate-y-1 hover:border-emerald-300 hover:shadow-md">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-600"><b.icon className="h-5 w-5" /></span>
                  <span className="text-sm font-semibold text-slate-800">{t(b.key)}</span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Products (subtle) */}
      <section className="container-page mt-20">
        <div className="flex flex-col items-start justify-between gap-4 rounded-2xl border border-slate-200 p-6 sm:flex-row sm:items-center">
          <div>
            <h2 className="font-heading text-xl font-bold text-slate-900">{t("productsTitle")}</h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-500">{t("productsSub")}</p>
          </div>
          <Button asChild variant="outline" className="rounded-full border-slate-300" data-testid="products-cta"><Link to={lp(`/${sp("products")}`)}>{t("browseProducts")}</Link></Button>
        </div>
      </section>

      <Newsletter />
    </>
  );
}
